<template>
  <div class="context-wrapper">
    <TextBody :text="title" />
    <div class="context-items-wrapper">
      <div class="gradient-container" v-if="hiddenButtons.length > 0" />
      <BbBtn size="large" type="secondary" class="remove-all-button" @click="resetTacticContextFilters">
        <div>
          {{ t('common.removeAll') }}
        </div>
        <BbBtn size="x-small" type="icon" icon="delete" icon-size="medium" no-background />
      </BbBtn>
      <BbBtn v-for="tactic in tacticContextFilters" :key="tactic.id" size="large" type="secondary">
        {{ tactic.regionDescriptionName }}, {{ tactic.name }}
        <BbBtn
          size="x-small"
          type="icon"
          icon="x-small"
          icon-size="medium"
          no-background
          :id="`close-btn-${tactic.id}`"
          @click="onRemoveTacticContext(tactic)"
        />
      </BbBtn>
      <BbBtn
        v-if="hiddenButtons.length > 0"
        size="x-small"
        type="icon"
        icon="chevron-down"
        icon-size="medium"
        class="popover-items-button"
        @click="handleOpenMenu(true)"
      />
      <BbPopover
        :model-value="showMenu"
        :hideCloseButton="true"
        location="bottom"
        width="30%"
        @update:modelValue="handleOpenMenu(false)"
      >
        <template #popover>
          <TacticItemsStyles />
        </template>
      </BbPopover>
    </div>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { computed, onMounted, onUnmounted, ref, watch, nextTick } from 'vue';

import useAuthoring from '~/composables/useAuthoring';
import TextBody from '~/components/common/TextBody.vue';
import TacticItemsStyles from '~/components/conversation/authoring/tacticsContexts/TacticContextStylesItems.vue';

export default {
  name: 'TacticsStyles',
  components: {
    TextBody,
    TacticItemsStyles,
  },

  setup() {
    const { t } = useI18n();
    const { tacticContextFilters, resetTacticContextFilters, setTacticContextFilters } = useAuthoring();

    const nrOfContexts = computed(() => Object.values(tacticContextFilters.value).length);
    const title = computed(() => `${t('author.filters.contextStyles')} (${nrOfContexts.value})`);
    const showMenu = ref(false);
    const hiddenButtons = ref([]);
    const buttons = computed(() => {
      const ids = Object.values(tacticContextFilters.value).map((item) => ({ id: `close-btn-${item.id}` }));
      return ids;
    });

    let observer;

    const initializeObserver = () => {
      if (observer) observer.disconnect();

      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const buttonId = entry.target.id;
            const isButtonVisible = entry.isIntersecting;

            if (!isButtonVisible) {
              if (!hiddenButtons.value.includes(buttonId)) {
                hiddenButtons.value.push(buttonId);
              }
            } else {
              // Remove button from non-visible list if it becomes visible
              hiddenButtons.value = hiddenButtons.value.filter((id) => id !== buttonId);
            }
          });
        },
        { threshold: 0.1 },
      );

      // Observe all buttons
      buttons.value.forEach((btn) => {
        const el = document.getElementById(btn.id);
        if (el) {
          observer.observe(el);
        }
      });
    };

    onMounted(async () => {
      await nextTick();
      initializeObserver();
    });

    onUnmounted(() => {
      if (observer) observer.disconnect();
    });

    const onRemoveTacticContext = (tacticContext) => {
      setTacticContextFilters({ contextTypeId: tacticContext.contextTypeId, tacticContext });
    };

    watch(
      tacticContextFilters,
      async () => {
        await nextTick();
        initializeObserver();
      },
      { deep: true },
    );

    const handleOpenMenu = async (value) => {
      showMenu.value = value;
    };

    return {
      t,
      tacticContextFilters,
      showMenu,
      handleOpenMenu,
      title,
      hiddenButtons,
      onRemoveTacticContext,
      resetTacticContextFilters,
    };
  },
};
</script>

<style lang="scss" scoped>
.context-wrapper {
  width: 100%;
  height: 80px;
  background: $bb-neutral-0;
  display: flex;
  flex-direction: column;
  gap: 9px;
  color: #1a1a1a;
  font-size: 15px;
  font-style: normal;

  .context-items-wrapper {
    display: flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
    position: relative;

    .gradient-container {
      background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #fff 100%);
      position: absolute;
      right: 0;
      top: 0;
      width: 100%;
      height: 100%;
      z-index: 1;
    }

    .remove-all-button {
      z-index: 4;
    }

    .popover-items-button {
      position: absolute;
      right: 0;
      top: 0;
      width: 40px !important;
      height: 40px !important;
      z-index: 2;
    }

    .bb-btn.secondary {
      :deep(.v-btn__content) {
        display: flex;
        align-items: center;

        div {
          font-weight: 500;
          color: #1c1c1d;
        }

        .bb-btn.icon {
          &:hover {
            background: transparent;
          }

          i {
            font-size: 18px;
          }
        }
      }

      &:hover {
        background: transparent;
        cursor: initial;
      }
    }
  }
}
</style>
